import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { LoaderIcon, CreditCard, ShieldCheck, CalendarClock } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { stripeConfig } from "@/config/billing";
import BillingAPIService from "@/services/billing-api";
import { Skeleton } from "@/components/ui/skeleton";
import { useCountry } from "@/contexts/CountryContext";

const stripePromise = loadStripe(stripeConfig.publicKey);

interface SubscriptionPaymentProps {
  planId: number;
  planName?: string;
  amount?: number;
  billingPeriod: "monthly" | "annual";
  userEmail: string;
  flowData: any;
  clientSecret?: string;
}

const SubscriptionPaymentForm: React.FC<SubscriptionPaymentProps> = ({
  planName,
  amount,
  billingPeriod,
  flowData,
  clientSecret,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  const { formatCurrency } = useCountry();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    try {
      setIsLoading(true);
      setError(undefined);

      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      // Encode subscription data for URL
      const subscriptionData = {
        email: flowData.email,
        password: flowData.password,
        billingTierId: flowData.tier.id,
        billingPeriod: flowData.tier.billing_period,
        affiliateCode: flowData.options?.affiliateCode,
        subscription_type: flowData.plan?.plan_type,
      };

      const encodedData = btoa(JSON.stringify(subscriptionData));
      const returnUrl = `${window.location.origin}/subscription/complete?data=${encodedData}`;

      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          payment_method_data: {
            billing_details: {
              email: flowData.email,
            },
          },
          return_url: returnUrl,
        },
      });

      if (result.error) {
        throw new Error(result.error.message);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Payment processing failed";
      setError(message);
      toast({
        title: "Payment Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Hero section with themed background */}
      <div className="bg-secondary text-secondary-foreground rounded-lg p-6 shadow-lg text-center">
        <div className="inline-flex items-center justify-center bg-secondary-foreground/10 p-3 rounded-full w-16 h-16 mb-4">
          <CreditCard className="h-8 w-8" />
        </div>
        <h1 className="text-3xl font-bold mb-2">Complete Your Subscription</h1>
        <p className="opacity-90 max-w-md mx-auto">
          You're one step away from managing your nanny payroll with ease
        </p>
      </div>

      {/* Side by side summary and payment form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
        {/* Subscription Summary Card */}
        <Card className="shadow-md h-full border-secondary/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-primary">Subscription Summary</h3>
              <span className="bg-secondary/20 text-secondary-foreground text-xs px-2 py-1 rounded-full">
                {billingPeriod === "monthly" ? "Monthly" : "Annual"} Plan
              </span>
            </div>
            
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-muted">
              <div>
                <p className="font-medium">{planName}</p>
                <p className="text-sm text-muted-foreground">
                  Billed {billingPeriod === "monthly" ? "monthly" : "annually"}
                </p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-primary">{formatCurrency(amount || 0)}</p>
                <p className="text-xs text-muted-foreground">
                  {billingPeriod === "monthly" ? "per month" : "per year"}
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center text-sm">
                <CalendarClock className="h-4 w-4 mr-2 text-secondary" />
                <span>{billingPeriod === "monthly" ? "Auto-renews monthly" : "Annual subscription"}</span>
              </div>
              <div className="flex items-center text-sm">
                <ShieldCheck className="h-4 w-4 mr-2 text-secondary" />
                <span>Cancel anytime, no long-term commitment</span>
              </div>
              <div className="mt-8 pt-8 border-t border-muted">
                <div className="flex items-center justify-center text-sm text-muted-foreground">
                  <ShieldCheck className="h-4 w-4 mr-2 text-secondary" />
                  <span>Your payment is processed securely through Stripe</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment form */}
        <Card className="shadow-md h-full border-secondary/20">
          <CardContent className="pt-6">
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-primary mb-2">Payment Details</h3>
                <p className="text-sm text-muted-foreground mb-4">Enter your card information below</p>
                <div data-testid="stripe-payment-element">
                  <PaymentElement
                    options={{
                      layout: {
                        type: 'tabs',
                        defaultCollapsed: false,
                      },
                    }}
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 py-6 mt-4"
                size="lg"
                disabled={isLoading || !stripe}
                data-testid="complete-subscription-button"
              >
                {isLoading ? (
                  <>
                    <LoaderIcon className="mr-2 h-5 w-5 animate-spin" />
                    Processing Payment...
                  </>
                ) : (
                  "Complete Subscription"
                )}
              </Button>
              
              <p className="text-xs text-center text-muted-foreground mt-4">
                By subscribing, you agree to our Terms of Service and Privacy Policy
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export const SubscriptionPayment: React.FC<SubscriptionPaymentProps> = (
  props,
) => {
  const [clientSecret, setClientSecret] = useState<string>();

  React.useEffect(() => {
    const initializeStripe = async () => {
      const planInfo = {
        billingTierId: props.flowData?.tier?.id,
        planName: props.flowData?.plan?.name,
        discountCode: props.flowData?.options?.discountCode,
        affiliateCode: props.flowData?.options?.affiliateCode,
      };
      
      const result = await BillingAPIService.createSetupIntent(props.userEmail, planInfo);
      setClientSecret(result.clientSecret);
    };

    initializeStripe();
  }, [props.userEmail, props.flowData]);

  if (!clientSecret) {
    return (
      <div className="space-y-8">
        <div className="bg-secondary/60 text-secondary-foreground rounded-lg p-6 shadow-lg text-center">
          <Skeleton className="h-16 w-16 rounded-full mx-auto mb-4" />
          <Skeleton className="h-8 w-64 mx-auto mb-2" />
          <Skeleton className="h-4 w-full max-w-md mx-auto" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          <Skeleton className="h-80 w-full rounded-lg" />
          <Skeleton className="h-80 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  const options = {
    clientSecret,
    appearance: stripeConfig.options.appearance,
    paymentMethodCreation: "manual" as const,
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <SubscriptionPaymentForm {...props} clientSecret={clientSecret} />
    </Elements>
  );
};

export default SubscriptionPayment;
